import { Document, Page, Text, View } from '@react-pdf/renderer'
import { PdfPreparedFor, tw } from '~/components/pdfs'
import {
  type Company,
  type Listing,
  type ListingBroker,
  type ListingTransaction,
  type Organization,
  type ReportPdfOptions,
  type User,
} from '~/models'
import { cn, fmtNumberAbbr } from '~/utils'
import {
  CompChart,
  CompStats,
  CompTable,
  PdfBrokerFooter,
  type CompChartConfig,
  type CompStatsConfig,
  type CompTableConfig,
} from './components'
import { PdfDisclaimersPage, ReportPdfTitlePage } from './pages'
import { getCompsStatsData, getSoldTransactionsData } from './utils'

// Sold Comps Chart Configuration Function
const getSoldChartConfig = (isLandReport: boolean): CompChartConfig => {
  return {
    bars: [
      {
        dataKey: 'soldPricePerSf',
        fill: '#a78bfa',
        name: 'Sold Price/SF',
      },
      {
        dataKey: 'listedPricePerSf',
        fill: '#818cf8',
        name: 'Listed Price/SF',
      },
    ],
    yAxisWidth: 60,
    yAxisFormatter: (value: number) => `$${value}/SF`,
  }
}

// Sold Comps Stats Configuration Function
const getSoldStatsConfig = (isLandReport: boolean): CompStatsConfig[] => {
  const statsConfig: CompStatsConfig[] = [
    {
      key: 'averageSoldPrice',
      label: 'Avg. Sold Price',
      formatter: (value: number) => `$${Math.round(value).toLocaleString()}`,
    },
    {
      key: 'averageSoldPricePerSf',
      label: 'Avg. Sold $/SF',
      formatter: (value: number) => `$${Math.round(value).toLocaleString()}/SF`,
    },
  ]

  if (isLandReport) {
    statsConfig.push(
      {
        key: 'averageSoldPricePerAcre',
        label: 'Avg. Sold $/Acre',
        formatter: (value: number) =>
          `$${Math.round(value).toLocaleString()}/ac`,
      },
      {
        key: 'averageAcres',
        label: 'Avg. Lot Size',
        formatter: (value: number) =>
          value > 100
            ? `${Math.round(value).toLocaleString()} ac`
            : `${value.toFixed(2)} ac`,
      }
    )
  } else {
    statsConfig.push(
      {
        key: 'averageListedPrice',
        label: 'Avg. Listed Price',
        formatter: (value: number) => `$${Math.round(value).toLocaleString()}`,
      },
      {
        key: 'averageListedPricePerSf',
        label: 'Avg. Listed $/SF',
        formatter: (value: number) =>
          `$${Math.round(value).toLocaleString()}/SF`,
      },
      {
        key: 'averageBuildingSf',
        label: 'Avg. Building SF',
        formatter: (value: number) =>
          `${Math.round(value).toLocaleString()} SF`,
      }
    )
  }

  return statsConfig
}

// Sold Comps Table Configuration Function
const getSoldTableConfig = (isLandReport: boolean): CompTableConfig => {
  const baseColumns = [
    {
      key: 'address',
      label: 'Address',
    },
  ]

  const landReportColumns = [
    {
      key: 'soldPrice',
      label: 'Sold Price',
      formatter: (value: any) =>
        value ? `$${(value as number).toLocaleString()}` : '-',
    },
    {
      key: 'soldPricePerAcre',
      label: 'S. Price/Ac',
      formatter: (value: any) =>
        value ? `$${fmtNumberAbbr(value as number)}/ac` : '-',
    },
    {
      key: 'soldPricePerSf',
      label: 'S. Price/SF',
      formatter: (value: any) =>
        value ? `$${(value as number).toLocaleString()}/SF` : '-',
    },
    {
      key: 'acres',
      label: 'Acres',
      formatter: (value: any) =>
        value ? `${(value as number).toLocaleString()} ac` : '-',
    },
    {
      key: 'lotSizeSf',
      label: 'Lot Size SF',
      formatter: (value: any) =>
        value ? `${(value as number).toLocaleString()} SF` : '-',
    },
  ]

  const buildingReportColumns = [
    {
      key: 'soldPrice',
      label: 'Sold Price',
      formatter: (value: any) =>
        value ? `$${(value as number).toLocaleString()}` : '-',
    },
    {
      key: 'soldPricePerSf',
      label: 'S. Price/SF',
      formatter: (value: any) =>
        value ? `$${(value as number).toLocaleString()}/SF` : '-',
    },
    {
      key: 'buildingSf',
      label: 'Building SF',
      formatter: (value: any) =>
        value ? `${(value as number).toLocaleString()} SF` : '-',
    },
  ]

  const endColumns = [
    {
      key: 'date',
      label: 'Date',
      basis: 1,
    },
  ]

  return {
    columns: [
      ...baseColumns,
      ...(isLandReport ? landReportColumns : buildingReportColumns),
      ...endColumns,
    ],
  }
}

export default function ReportSoldCompsPdf({
  organization,
  title,
  user,
  company,
  brokers,
  listings,
  pdfOptions,
}: {
  organization: Organization
  title: string
  user: User
  company?: Company
  brokers?: ListingBroker[]
  listings: Listing[]
  pdfOptions: ReportPdfOptions
}) {
  const soldTransactions: (ListingTransaction & {
    listing: Listing
  })[] = listings
    .map((listing) => {
      return (listing.transactions ?? []).map((transaction) => ({
        ...transaction,
        listing,
      }))
    })
    .flat()
    .filter((t) => t.sale)

  soldTransactions.sort((a, b) => {
    return new Date(b.date).getTime() - new Date(a.date).getTime()
  })

  const isLandReport =
    soldTransactions.filter((t) => t.listing.propertyTypes[0] === 'Land')
      .length >=
    soldTransactions.length - Math.round(soldTransactions.length / 4) // threshold?

  const soldData = getSoldTransactionsData(soldTransactions, isLandReport)
  const statsData = getCompsStatsData(soldTransactions, isLandReport)

  return (
    <Document title={title}>
      {pdfOptions.showTitlePage && (
        <ReportPdfTitlePage
          title={title}
          company={company}
          user={user}
          listings={listings}
          pdfOptions={pdfOptions}
          orientation="portrait"
        />
      )}
      {soldTransactions.length > 0 && (
        <Page
          size="LETTER"
          style={tw('flex flex-col gap-4 px-8 pb-24 pt-8 font-sans text-sm')}
          orientation="portrait">
          <View
            style={tw(
              cn(
                !pdfOptions.showTitlePage &&
                  'flex flex-row justify-between gap-6'
              )
            )}>
            {!pdfOptions.showTitlePage && (
              <View style={tw('mb-3')}>
                <Text style={tw('font-heading mb-2 text-2xl leading-tight')}>
                  {title}
                </Text>
                <PdfPreparedFor />
              </View>
            )}

            <View style={tw(cn(!pdfOptions.showTitlePage && 'text-right'))}>
              <Text style={tw('text-xl font-bold')}>
                {soldTransactions.length} Sold Transactions
              </Text>
            </View>
          </View>
          <CompChart
            data={soldData}
            config={getSoldChartConfig(isLandReport)}
          />

          <CompStats
            statsData={statsData}
            config={getSoldStatsConfig(isLandReport)}
          />

          <CompTable
            data={soldData}
            config={getSoldTableConfig(isLandReport)}
          />

          <PdfBrokerFooter
            organization={organization}
            user={user}
            company={company}
            brokers={brokers}
          />
        </Page>
      )}

      <PdfDisclaimersPage
        pdfOptions={pdfOptions}
        organization={organization}
        orientation="portrait"
      />
    </Document>
  )
}
